import { createContext, useContext, useEffect, useState } from "react";
import { getSession, saveSession, clearSession, login as apiLogin, pingSession } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const u = getSession();
    setUser(u);
    setReady(true);
    // Returning visitor with a saved session: count the visit as a login (Airtable login_time) — once per day
    const rec = u?.user_record_id || u?.record_id;
    if (rec) {
      const k = `postcue.lastping.${rec}`;
      const today = new Date().toISOString().slice(0, 10);
      if (localStorage.getItem(k) !== today) {
        localStorage.setItem(k, today);
        pingSession(rec).catch(() => localStorage.removeItem(k));
      }
    }
  }, []);

  async function login(email, password, opts = {}) {
    const res = await apiLogin(email, password);
    if (!res?.success) {
      const err = new Error(res?.message || "Invalid email or password.");
      err.accountExists = !!res?.account_exists;
      throw err;
    }
    const u = res.user || {};
    saveSession(u, { remember: opts.remember !== false });
    setUser(u);
    return u;
  }

  function clearLocalCarts() {
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("postcue.cart.") || k.startsWith("postcue.coupon."))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      /* ignore */
    }
  }

  function logout() {
    clearSession();
    clearLocalCarts();
    setUser(null);
  }

  function adoptUser(u, opts = {}) {
    // Used by phone-OTP, signup and admin "switch user" flows after the backend
    // has already validated the credentials. A different account must never
    // inherit this browser's cart.
    const prev = user?.user_record_id || user?.record_id;
    const next = u?.user_record_id || u?.record_id;
    if (prev && next && prev !== next) clearLocalCarts();
    saveSession(u, { remember: opts.remember !== false });
    setUser(u);
  }

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, adoptUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
